import React, { useEffect, useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Alert, AlertDescription } from '../ui/alert'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import { getImpactoDeporte, deshabilitarDeporteOrg, habilitarDeporteOrg } from '../../lib/services/deportesProceso'
import { impedimentos, puedeConfirmar, resumenConteos, MOTIVO_MIN } from '../../lib/deporteCambio'

// Habilitar o deshabilitar UN deporte de una org. La vista previa sale de
// deporte_impacto; la base vuelve a revisar todo al confirmar.
const DeporteCambioDialog = ({ open, onOpenChange, org, deporte, accion, onHecho }) => {
  const [impacto, setImpacto] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [errorCarga, setErrorCarga] = useState(null)
  const [motivo, setMotivo] = useState('')
  const [confirmacion, setConfirmacion] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })

  useEffect(() => {
    if (!open || !org?.id || !deporte?.id) return
    let vivo = true
    setImpacto(null)
    setErrorCarga(null)
    setMotivo('')
    setConfirmacion('')
    setError(null)
    guard.resetDirty()
    setCargando(true)
    getImpactoDeporte(org.id, deporte.id).then(({ data, error: err }) => {
      if (!vivo) return
      if (err) setErrorCarga(err.message || 'No se pudo revisar el impacto.')
      else setImpacto(data)
      setCargando(false)
    })
    return () => { vivo = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, org?.id, deporte?.id])

  const deshabilitar = accion === 'deshabilitar'
  const nombre = deporte?.nombre || ''
  const bloqueos = impedimentos(accion, impacto)
  const lista = resumenConteos(deshabilitar ? impacto?.cierra : impacto?.restaura)
  const listo = puedeConfirmar({ accion, impacto, motivo, confirmacion, nombreDeporte: nombre })

  const confirmar = async () => {
    setGuardando(true)
    setError(null)
    const { data, error: err } = deshabilitar
      ? await deshabilitarDeporteOrg({ orgId: org.id, deporteId: deporte.id, motivo, confirmacion })
      : await habilitarDeporteOrg({ orgId: org.id, deporteId: deporte.id, motivo })
    setGuardando(false)
    if (err) {
      setError(err.message || 'No se pudo completar el cambio.')
      return
    }
    guard.resetDirty()
    onHecho?.(data)
    onOpenChange(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{deshabilitar ? `Deshabilitar ${nombre}` : `Habilitar ${nombre}`}</DialogTitle>
            <DialogDescription>{org?.name}</DialogDescription>
          </DialogHeader>

          {cargando && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Revisando el impacto...
            </div>
          )}
          {errorCarga && (
            <Alert variant="destructive"><AlertDescription>{errorCarga}</AlertDescription></Alert>
          )}

          {impacto && bloqueos.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {deshabilitar ? 'Primero hay que cerrar esto:' : 'No se puede habilitar:'}
              </p>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {bloqueos.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}

          {impacto && bloqueos.length === 0 && (
            <div className="space-y-4">
              <div className="text-sm">
                <p className="font-medium">{deshabilitar ? 'Se va a cerrar:' : 'Se va a reabrir:'}</p>
                {lista.length > 0 ? (
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {lista.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                ) : (
                  <p className="mt-1 text-muted-foreground">
                    {deshabilitar ? 'No hay nada activo de este deporte.' : 'Nada que reabrir.'}
                  </p>
                )}
                {deshabilitar && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    El historial (asistencias, juegos, cobros pagados) se conserva. Si se vuelve a
                    habilitar, esto se reabre.
                  </p>
                )}
              </div>

              {!impacto.tiene_owner && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>Esta organización no tiene Owner: nadie verá el aviso.</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="motivo-deporte">Motivo</Label>
                <Textarea
                  id="motivo-deporte"
                  rows={2}
                  value={motivo}
                  onChange={(e) => { guard.markDirty(); setMotivo(e.target.value) }}
                  placeholder={deshabilitar ? 'Ej. El club dejó de pagar este deporte' : 'Ej. El club contrató este deporte'}
                />
                <p className="text-xs text-muted-foreground">
                  Lo verá el Owner de la organización. Mínimo {MOTIVO_MIN} caracteres.
                </p>
              </div>

              {deshabilitar && (
                <div className="space-y-1.5">
                  <Label htmlFor="confirmar-deporte">Escribe {nombre} para confirmar</Label>
                  <Input
                    id="confirmar-deporte"
                    autoComplete="off"
                    value={confirmacion}
                    onChange={(e) => { guard.markDirty(); setConfirmacion(e.target.value) }}
                  />
                </div>
              )}
            </div>
          )}

          {error && (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)} disabled={guardando}>
              Cancelar
            </Button>
            <Button
              variant={deshabilitar ? 'destructive' : 'default'}
              onClick={confirmar}
              disabled={!listo || guardando}
            >
              {guardando ? 'Guardando...' : deshabilitar ? 'Deshabilitar' : 'Habilitar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.DiscardDialog}
    </>
  )
}

export default DeporteCambioDialog
